import { useState } from 'react';
import Badge from '@klv/atoms/badge';
import Icon from '@klv/atoms/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@klv/atoms/tooltip';
import { getDateTimeDiffFromNow } from '../utils';
import useIsHydrated from '@klv/hooks/useIsHydrated';
import useStartOfMinuteInterval from '../hooks/useStartOfMinuteInterval';
import { useTranslation } from '@klv/hooks/useTranslation';

import type { INextEvent } from '@klv/molecules/unified-search-results/types';

import * as s from './styles/CountdownBadge.module.css';

interface IProps {
  nextEvent: INextEvent;
}

const getCountdownAndTooltip = (
  nextEvent: INextEvent,
  t: (key: string, options?: Record<string, string | number>) => string,
) => {    
  const {
    nowDateTime,
    endDateTime,
    diff: {
      minutes,
      hours,
      days,
    },
  } = getDateTimeDiffFromNow(nextEvent?.start_time);

  if (!nowDateTime || !endDateTime) {
    return {
      countdown: '',
      tooltip: '',
    };
  }

  const localizedThisEventStartsIn = t('molecules_unifiedSearchResultGridItem_countdownBadge_tooltip_thisEventStartIn');
  const localizedDayOrDays = t(`molecules_unifiedSearchResultGridItem_countdownBadge_tooltip_day${days > 1 ? 's' : ''}`);
  const localizedHourOrHours = t(`molecules_unifiedSearchResultGridItem_countdownBadge_tooltip_hour${hours > 1 ? 's' : ''}`);
  const localizedMinuteOrMinutes = t(`molecules_unifiedSearchResultGridItem_countdownBadge_tooltip_minute${minutes > 1 ? 's' : ''}`);

  switch (true) {
    default:
    case days === 0 && hours === 0 && minutes === 0:
      return {
        countdown: t('molecules_unifiedSearchResultGridItem_countdownBadge_countdown_now'),
        tooltip: t('molecules_unifiedSearchResultGridItem_countdownBadge_tooltip_thisEventStartsNow'),
      };
    case days > 0:
      return {
        countdown: t('molecules_unifiedSearchResultGridItem_countdownBadge_countdown_d', { days }),
        tooltip: `${localizedThisEventStartsIn} ${days} ${localizedDayOrDays}`,
      };
    case hours > 0:
      return {
        countdown: t('molecules_unifiedSearchResultGridItem_countdownBadge_countdown_h', { hours }),
        tooltip: `${localizedThisEventStartsIn} ${hours} ${localizedHourOrHours}`,
      };
    case minutes > 0:
      return {
        countdown: t('molecules_unifiedSearchResultGridItem_countdownBadge_countdown_m',  { minutes }),
        tooltip: `${localizedThisEventStartsIn} ${minutes} ${localizedMinuteOrMinutes}`,
      };
  }
};

const CountdownBadge = ({ nextEvent }: IProps) => {
  const isHydrated = useIsHydrated();
  const { t } = useTranslation();

  const [countdownData, setCountdownData] = useState(getCountdownAndTooltip(nextEvent, t));

  const invalidContent = !countdownData.countdown && !countdownData.tooltip;

  const MINUTE_COUNTDOWN_REGEX = new RegExp(/^[1-9]{1,2}m$/);
  const isCountingMinutes = (MINUTE_COUNTDOWN_REGEX).test(countdownData.countdown);
  const shouldStartInterval = Boolean(
    isHydrated && nextEvent?.start_time && isCountingMinutes && !invalidContent,
  );
  
  useStartOfMinuteInterval(shouldStartInterval, (stop) => {
    const { countdown, tooltip } = getCountdownAndTooltip(nextEvent, t);

    if ((!countdown && !tooltip) || !((MINUTE_COUNTDOWN_REGEX).test(countdown))) {
      stop();
    }
    setCountdownData({ countdown, tooltip });
  });

  // * Check for hydration: local time is different on server vs. client
  if (!isHydrated || !nextEvent?.start_time || invalidContent) {
    return <></>;
  }

  const { countdown, tooltip } = countdownData;

  return (
    <Tooltip placement='top'>
      <TooltipTrigger>
        <Badge color='poppy'>
          <div className={s.badgeContainer}>
            <Icon iconName='ClockOutline' size={1.5} fillColor='#fff' />
            <p className={s.alertCountdown}>
              {countdown}
            </p>
          </div>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className={s.tooltipContent}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

export default CountdownBadge;
